import { heicTo, isHeic } from 'heic-to'

// Configuration constants
const DEFAULT_MIME_TYPE = 'image/webp'
const DEFAULT_MAX_SIZE_KB = 50
const DEFAULT_MAX_WIDTH = 400
const DEFAULT_MAX_HEIGHT = 400
const INITIAL_QUALITY = 0.8
const QUALITY_STEP = 0.05
const MIN_QUALITY = 0.1
const TIME_IN_SECONDS = 1

// HEIC conversion settings
const HEIC_PNG_QUALITY = 0.95 // Slightly lower for better performance
const HEIC_JPEG_QUALITY = 0.9 // Fallback quality

function compressCanvasToBlob(
    canvas: HTMLCanvasElement,
    maxSizeKB = DEFAULT_MAX_SIZE_KB,
    maxWidth = DEFAULT_MAX_WIDTH,
    maxHeight = DEFAULT_MAX_HEIGHT,
    mimeType = DEFAULT_MIME_TYPE
): Promise<Blob> {
    return new Promise((resolve, reject) => {
        let quality = INITIAL_QUALITY

        const scaleCanvas = (): HTMLCanvasElement => {
            const { width: originalWidth, height: originalHeight } = canvas
            let width = originalWidth
            let height = originalHeight

            // Calculate aspect ratio preserving scaling
            const aspectRatio = originalWidth / originalHeight

            if (width > maxWidth || height > maxHeight) {
                if (aspectRatio > maxWidth / maxHeight) {
                    // Width is the limiting factor
                    width = maxWidth
                    height = Math.floor(width / aspectRatio)
                } else {
                    // Height is the limiting factor
                    height = maxHeight
                    width = Math.floor(height * aspectRatio)
                }
            }

            // Skip resizing if dimensions haven't changed
            if (width === originalWidth && height === originalHeight) {
                return canvas
            }

            const resizedCanvas = document.createElement('canvas')
            resizedCanvas.width = width
            resizedCanvas.height = height

            const ctx = resizedCanvas.getContext('2d')
            if (!ctx) {
                throw new Error('Failed to get 2D context')
            }

            // Use better image smoothing
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'

            ctx.drawImage(
                canvas,
                0,
                0,
                originalWidth,
                originalHeight,
                0,
                0,
                width,
                height
            )

            return resizedCanvas
        }

        const resizedCanvas = scaleCanvas()

        // Use binary search for faster quality finding
        const minQuality = MIN_QUALITY
        let maxQuality = INITIAL_QUALITY
        let attempts = 0
        const maxAttempts = 10

        const compress = () => {
            resizedCanvas.toBlob(
                (blob) => {
                    if (!blob) {
                        return reject(
                            new Error(
                                'Compression failed: Unable to create blob'
                            )
                        )
                    }

                    const sizeKB = blob.size / 1024

                    if (
                        sizeKB <= maxSizeKB ||
                        attempts >= maxAttempts ||
                        quality <= MIN_QUALITY
                    ) {
                        resolve(blob)
                    } else {
                        attempts++
                        // Binary search for optimal quality
                        if (sizeKB > maxSizeKB * 1.5) {
                            // File is much larger, reduce quality more aggressively
                            maxQuality = quality
                            quality = (minQuality + quality) / 2
                        } else {
                            // File is close to target, reduce quality gradually
                            quality -= QUALITY_STEP
                        }
                        compress()
                    }
                },
                mimeType,
                quality
            )
        }

        console.log(maxQuality)

        compress()
    })
}

// Check if file is HEIC format with caching
const heicCheckCache = new WeakMap<File, boolean>()
async function isHEICFormat(file: File): Promise<boolean> {
    // Check cache first
    if (heicCheckCache.has(file)) {
        return heicCheckCache.get(file)!
    }

    // Quick check by file extension first (faster)
    const hasHeicExtension = file.name.toLowerCase().match(/\.(heic|heif)$/)
    if (!hasHeicExtension) {
        heicCheckCache.set(file, false)
        return false
    }

    // Verify with library for accuracy
    const result = await isHeic(file)
    heicCheckCache.set(file, result)
    return result
}

// Convert HEIC with optimized settings and fallback
async function convertHEICToPNG(file: File): Promise<Blob> {
    try {
        // Try PNG first (better quality)
        return await heicTo({
            blob: file,
            type: 'image/png',
            quality: HEIC_PNG_QUALITY,
        })
    } catch (pngError: any) {
        console.warn(
            'PNG conversion failed, trying JPEG fallback:',
            pngError.message
        )

        try {
            // Fallback to JPEG (better compatibility)
            return await heicTo({
                blob: file,
                type: 'image/jpeg',
                quality: HEIC_JPEG_QUALITY,
            })
        } catch (jpegError: any) {
            console.error('Both PNG and JPEG conversion failed:', jpegError)

            // Provide helpful error message
            if (jpegError.message?.includes('format not supported')) {
                throw new Error(
                    'This HEIC file format is not supported. Please try converting it with a different tool.'
                )
            }
            throw new Error(
                `HEIC conversion failed: ${jpegError.message || 'Unknown error'}`
            )
        }
    }
}

export async function getThumbnailFile(
    file: File,
    thumbnailName?: string
): Promise<File> {
    const fileType = file.type.split('/')[0]

    const createFileFromBlob = (blob: Blob): File =>
        new File([blob], thumbnailName || crypto.randomUUID(), {
            type: DEFAULT_MIME_TYPE,
        })

    // Handle HEIC files specially
    if (await isHEICFormat(file)) {
        try {
            const convertedBlob = await convertHEICToPNG(file)
            // Detect the actual type of the converted blob
            const mimeType = convertedBlob.type || 'image/png'
            const convertedFile = new File(
                [convertedBlob],
                file.name.replace(
                    /\.(heic|heif)$/i,
                    mimeType === 'image/png' ? '.png' : '.jpg'
                ),
                { type: mimeType }
            )
            // Process the converted file (recursive call will handle it as regular image)
            return getThumbnailFile(convertedFile, thumbnailName)
        } catch (error: any) {
            console.error('HEIC thumbnail generation failed:', error)
            return Promise.reject(
                new Error(
                    `Cannot generate thumbnail for HEIC file: ${error.message || error}`
                )
            )
        }
    }

    if (fileType === 'image') {
        return new Promise((resolve, reject) => {
            const img = new Image()
            const objectUrl = URL.createObjectURL(file)

            img.src = objectUrl
            img.crossOrigin = 'anonymous'

            const cleanup = () => {
                URL.revokeObjectURL(objectUrl)
            }

            img.onload = async () => {
                try {
                    const canvas = document.createElement('canvas')
                    canvas.width = img.width
                    canvas.height = img.height

                    const ctx = canvas.getContext('2d')
                    if (!ctx) {
                        throw new Error('Failed to get canvas context')
                    }

                    ctx.drawImage(img, 0, 0)

                    const blob = await compressCanvasToBlob(
                        canvas,
                        DEFAULT_MAX_SIZE_KB
                    )
                    cleanup()
                    resolve(createFileFromBlob(blob))
                } catch (error) {
                    cleanup()
                    reject(error)
                }
            }

            img.onerror = () => {
                cleanup()
                reject(
                    new Error(
                        `Failed to load image: ${file.name}. Format may not be supported.`
                    )
                )
            }
        })
    }

    if (fileType === 'video') {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video')
            const objectUrl = URL.createObjectURL(file)

            video.src = objectUrl
            video.crossOrigin = 'anonymous'

            const cleanup = () => {
                URL.revokeObjectURL(objectUrl)
            }

            video.onloadeddata = () => {
                video.currentTime = TIME_IN_SECONDS
            }

            video.onseeked = async () => {
                try {
                    const canvas = document.createElement('canvas')
                    canvas.width = video.videoWidth
                    canvas.height = video.videoHeight

                    const ctx = canvas.getContext('2d')
                    if (!ctx) {
                        throw new Error('Failed to get canvas context')
                    }

                    ctx.drawImage(video, 0, 0)

                    const blob = await compressCanvasToBlob(
                        canvas,
                        DEFAULT_MAX_SIZE_KB
                    )
                    cleanup()
                    resolve(createFileFromBlob(blob))
                } catch (error) {
                    cleanup()
                    reject(error)
                }
            }

            video.onerror = () => {
                cleanup()
                reject(
                    new Error(
                        `Failed to load video: ${file.name}. Format may not be supported.`
                    )
                )
            }
        })
    }

    return Promise.reject('Unsupported file type')
}

export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
}

export const downloadThumbnailFromPresignedUrl = async (
    presignedUrl: string,
    thumbnailName?: string
): Promise<File> => {
    //Download the file using the presigned GET URL
    const response = await fetch(presignedUrl)

    if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`)
    }

    //Convert the response into a Blob and then to a File
    const blob = await response.blob()
    const fileName = 'downloaded-file'
    const file = new File([blob], fileName, { type: blob.type })

    //Generate and return the thumbnail
    return await getThumbnailFile(file, thumbnailName)
}
